import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert } from '@/components/ui/alert'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import Modal, { hideModal, showModal } from '../../widgets/modal'
import { Formik } from 'formik'
import { object as YupObject, string as YupString } from 'yup'
import classNames from 'classnames'
import MultiSelect from '../../widgets/MultiSelect'
import {
	updateAccountUser,
	saveAccountUser,
	fetchAccountUsers,
	fetchBackOfficeAccounts,
} from '../Account/account.asyncActions'
import { actions } from '../Account/account.slice'
import {
	SelectAddUserModal,
	SelectBackOfficeAccounts,
	selectSelectedRow,
} from '../Account/account.selector'
import {
	USER_ROLE_OPTIONS,
	STATUS_OPTIONS,
	STATUS_ENUMS,
	USER_ROLES_ENUMS,
} from '../Account/account.constants'
import BusyIndicator, { REGIONS } from '../../widgets/busyIndicator'
import { selectUser } from '../Profile/profile.selectors'
import { isEmpty } from 'lodash'
import {
	getFormattedTitle,
	getFormattedAccountName,
} from '../Reports/reports.constants'

import Form from '@/components/forms/legacy-form-shim'

const { setAccountModal, resetAddUserModal, setEmail, setSelectedRow } = actions
const BAD_REQUEST_ERROR = 400
const timeOutForAlertPrompt = 3000
const DUPLICATE_USER = 'User Already Exist'

const EditModal = ({ setNotifyToggle }) => {
	const dispatch = useDispatch()
	const backOfficeAccounts = useSelector(SelectBackOfficeAccounts)
	const resetModal = () => {
		dispatch(setAccountModal(null))
		dispatch(resetAddUserModal(false))
		dispatch(setSelectedRow({}))
	}
	const isAddUserModal = useSelector(SelectAddUserModal)
	const selectedRow = useSelector(selectSelectedRow)
	const user = useSelector(selectUser)
	const [isLength, setIsLength] = useState(false)
	let count = 0

	const [
		shoudlShowUserExistsErrorMessage,
		setShouldShowUserExistsErrorMessage,
	] = useState('')

	const { id, name, email, userRole, status, accounts } = selectedRow

	let ASSIGNMENT_OPTIONS = []
	ASSIGNMENT_OPTIONS =
		backOfficeAccounts &&
		backOfficeAccounts.map((each) => {
			return {
				...ASSIGNMENT_OPTIONS,
				value: each.id,
				title: getFormattedTitle(each.name),
				label: getFormattedAccountName(each.id, { account: each.name }),
			}
		})

	let ASSIGNED_ACCOUNTS = []
	ASSIGNED_ACCOUNTS =
		accounts &&
		accounts.map((each) => {
			return {
				...ASSIGNED_ACCOUNTS,
				value: each.id,
				title: getFormattedTitle(each.name),
				label: getFormattedAccountName(each.id, { account: each.name }),
			}
		})

	const getEditUserAssignments = () => {
		const uniqueAssignedAccounts = [...ASSIGNED_ACCOUNTS, ...ASSIGNMENT_OPTIONS]
		return uniqueAssignedAccounts.filter(
			(currentValue, index, arr) =>
				arr.findIndex((each) => each.value === currentValue.value) === index
		)
	}

	useEffect(() => {
		dispatch(fetchBackOfficeAccounts())
		dispatch(showModal())
	}, [dispatch])

	useEffect(() => {
		shoudlShowUserExistsErrorMessage &&
			setTimeout(() => {
				setShouldShowUserExistsErrorMessage('')
			}, timeOutForAlertPrompt)
	}, [dispatch, shoudlShowUserExistsErrorMessage])

	const handleClose = () => {
		setShouldShowUserExistsErrorMessage('')
		dispatch(hideModal())
		resetModal()
	}

	const validationSchema = YupObject().shape({
		name: YupString()
			.required('User Name is required')
			.min(3, 'User Name must have atleast 3 characters')
			.max(100, 'User Name should not exceed 100 characters'),
		email: YupString()
			.email('E-mail is Invalid')
			.required('E-mail is required'),
		userRole: YupString().required('User Role is required'),
		status: YupString().required('Status is required'),
	})

	const initialValues = {
		name: name || '',
		email: email || '',
		userRole,
		accounts: isAddUserModal ? [] : ASSIGNED_ACCOUNTS,
		status: isAddUserModal
			? STATUS_ENUMS.ACTIVE
			: selectedRow.lastSignIn !== null
				? status
				: '',
	}

	const getFormattedAssignedAccounts = (values) =>
		values.map((each) => each.value)

	const handleInputChange = (inputValue) => {
		inputValue.length > 0 && setIsLength(true)
		inputValue.length === 0 && setIsLength(false)
		if (inputValue.length === 0 && count === 0 && isLength) {
			dispatch(
				fetchBackOfficeAccounts({ filters: inputValue, useCaching: false })
			)
			count = 1
		}
		if (!isEmpty(inputValue) && inputValue.length > 2) {
			dispatch(
				fetchBackOfficeAccounts({ filters: inputValue, useCaching: false })
			)
			count = 0
		}
	}

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={(values, { setSubmitting, resetForm }) => {
				if (!isAddUserModal) {
					dispatch(
						updateAccountUser({
							...selectedRow,
							...values,
							userRole: parseInt(values.userRole),
							status: parseInt(values.status),
							accounts:
								parseInt(values.userRole) === USER_ROLES_ENUMS.SALES
									? getFormattedAssignedAccounts(values.accounts)
									: [],
						})
					).then(({ payload }) => {
						payload && dispatch(fetchAccountUsers({ useCaching: false }))
						dispatch(setAccountModal(null))
						dispatch(setSelectedRow({}))
						resetForm()
						setSubmitting(false)
					})
				} else {
					dispatch(setEmail(values.email))
					dispatch(
						saveAccountUser({
							name: values.name,
							email: values.email,
							userRole: parseInt(values.userRole),
							accounts: getFormattedAssignedAccounts(values.accounts),
							backOfficeUserId: user.id,
						})
					).then(async (res) => {
						const { payload } = res
						const isError = payload && payload.status && payload.status >= 400
						if (isError) {
							let errorMsg = 'Unable to add user. Please try again later.'
							try {
								const formattedPayloadText = await payload.text()
								const formattedPayload = JSON.parse(formattedPayloadText)
								if (formattedPayload?.message === DUPLICATE_USER) {
									errorMsg = 'User already exists. Please try with another email address.'
								}
							} catch { /* use default message */ }
							setShouldShowUserExistsErrorMessage(errorMsg)
						} else if (payload) {
							setNotifyToggle({ show: true })
							dispatch(fetchAccountUsers({ useCaching: false }))
							dispatch(setAccountModal(null))
							resetForm()
						}
						setSubmitting(false)
					})
				}
			}}
		>
			{({
				values,
				errors,
				touched,
				isSubmitting,
				handleChange,
				handleBlur,
				handleSubmit,
			}) => {
				const footer = (
					<div className='align-item-right flex gap-2'>
						<Button
							className='h-50 w-190'
							variant='outline'
							onClick={handleClose}
						>
							Cancel
						</Button>
						<Button
							className='h-50 w-190'
							type='submit'
							onClick={handleSubmit}
							disabled={isSubmitting}
						>
							<BusyIndicator
								region={
									isAddUserModal
										? REGIONS.ADD_ACCOUNT_USER
										: REGIONS.EDIT_ACCOUNT_USER
								}
							>
								{isAddUserModal ? 'Add' : 'Update'}
							</BusyIndicator>
						</Button>
					</div>
				)

				return (
					<Modal
						footer={footer}
						title='Manage User'
						reset={resetModal}
						size='sm'
					>
							{shoudlShowUserExistsErrorMessage && (
								<Alert variant='destructive'>{shoudlShowUserExistsErrorMessage}</Alert>
							)}
							<div className='space-y-4'>
								<div className={'form-group'}>
									<Label>User Name</Label>
									<Input
										type='text'
										name='name'
										placeholder='User Name'
										onChange={handleChange}
										onBlur={handleBlur}
										value={values.name}
										className={classNames({
											error: touched.name && errors.name,
										})}
									/>
									{touched.name && errors.name && (
										<div className='error-message'>{errors.name}</div>
									)}
								</div>
								<div className={'form-group'}>
									<Label>User Email</Label>
									<Input
										type='email'
										name='email'
										placeholder='User Email'
										onChange={(e) => {
											handleChange(e)
											setShouldShowUserExistsErrorMessage(false)
										}}
										onBlur={handleBlur}
										value={values.email}
										readOnly={!isAddUserModal}
										className={classNames({
											error: touched.email && errors.email,
										})}
									/>
									{touched.email && errors.email && (
										<div className='error-message'>{errors.email}</div>
									)}
								</div>
								<div className={'form-group'}>
									<Label>User Role</Label>
									<Select
										value={String(values.userRole)}
										onValueChange={(val) => handleChange({ target: { name: 'userRole', value: val } })}
										disabled={id === user.id}
									>
										<SelectTrigger className='w-full'>
											<SelectValue placeholder='Select role' />
										</SelectTrigger>
										<SelectContent>
											{USER_ROLE_OPTIONS.filter((each) => each.value !== '').map((each) => (
												<SelectItem key={each.value} value={String(each.value)}>
													{each.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									{touched.userRole && errors.userRole && (
										<div className='error-message'>{errors.userRole}</div>
									)}
								</div>
							{parseInt(values.userRole) === USER_ROLES_ENUMS.SALES && (
								<Form.Group classname={'form-group'}>
									<Form.Label>Account Assignment</Form.Label>
									<h6>
										Search and select unassigned accounts using the field below.
									</h6>
									<MultiSelect
										name={'accounts'}
										options={
											isAddUserModal
												? ASSIGNMENT_OPTIONS
												: getEditUserAssignments()
										}
										value={values.accounts}
										onInputChange={handleInputChange}
										onChange={(selectedOption) => {
											const event = {
												target: {
													name: 'accounts',
													value: selectedOption,
												},
											}
											handleChange(event)
										}}
										onBlur={() => {
											handleBlur({ target: { name: 'accounts' } })
										}}
										placeholder={'Search Account...'}
									/>
								</Form.Group>
							)}
							{!isAddUserModal && (
								<Form.Group classname={'form-group'}>
									<Form.Label>Status</Form.Label>
									<Form.Control
										as='select'
										name='status'
										value={values.status}
										onChange={handleChange}
										onBlur={handleBlur}
										className='pointer'
									>
										{STATUS_OPTIONS.map((each) => (
											<option key={each.value} value={each.value}>
												{each.label}
											</option>
										))}
									</Form.Control>
									{touched.status && errors.status && (
										<div className='error-message'>{errors.status}</div>
									)}
								</Form.Group>
							)}
						</div>
					</Modal>
				)
			}}
		</Formik>
	)
}

export default EditModal
